import { faTableLayout } from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";
import React from "react";
import { Controller, useForm } from "react-hook-form";

import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { Textarea } from "src/base-components/Textarea";
import { DEFAULT_STRING_LENGTH } from "src/base-components/schemaKit/validators";
import { IconPicker } from "src/cases/common/IconPicker";
import {
  useCaseTemplate,
  useUpdateCaseTemplate,
} from "src/cases/queries/templates";
import { AVAILABLE_ICONS } from "src/cases/templates/utils";
import { JsonApiResourceObjectCaseTemplateAttributesBaseModel as CaseTemplate } from "src/clients/case-management";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";

export type CaseTemplateModalProps = {
  open: boolean;
  onClose: () => void;
  templateId?: string;
};

export const CaseTemplateModal: React.FC<CaseTemplateModalProps> = ({
  open,
  onClose,
  templateId,
}) => {
  const { data: template, isLoading: isLoadingTemplate } =
    useCaseTemplate(templateId);

  const { mutateAsync: updateTemplate, isPending: isUpdating } =
    useUpdateCaseTemplate();

  const form = useForm<CaseTemplate>({
    mode: "onBlur",
    values: template,
  });

  const onSubmit = async (data: CaseTemplate) => {
    if (!template) return;

    try {
      await updateTemplate({
        id: template.id,
        etag: template.attributes.etag ?? 0,
        ...data.attributes,
      });

      onClose();
    } catch (error) {
      logger.error(error);
      if (isPreconditionError(error)) {
        toastActions.failure({
          title: "Failed to update case template",
          description:
            "Someone made changes while you were editing. Please refresh and try again.",
        });
        return;
      }

      if (isAxiosError(error)) {
        toastActions.failure({
          title: "Failed to update case template",
          description:
            error.response?.data.detail.msg ??
            "An unknown error occurred. Please try again later.",
        });
        return;
      }

      toastActions.failure({
        title: "Failed to update case template",
        description: "An unknown error occurred. Please try again later.",
      });
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header>{templateId ? "Edit" : "Create"} template</Modal.Header>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Modal.Content>
          <div className="flex flex-col content-stretch items-start justify-start gap-3">
            <div className="flex w-full items-start justify-start gap-2">
              <Controller
                control={form.control}
                defaultValue="shield-halved"
                name="attributes.icon"
                render={({ field }) =>
                  isLoadingTemplate ? (
                    <SkeletonPlaceholder height="h-15" width="w-15" />
                  ) : (
                    <IconPicker
                      options={AVAILABLE_ICONS}
                      placeholder={faTableLayout}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )
                }
              />
              <div className="flex flex-1 flex-col">
                <Label required>Name</Label>
                {isLoadingTemplate ? (
                  <SkeletonPlaceholder height="h-8" width="w-full" />
                ) : (
                  <>
                    <Input
                      errored={
                        !!form.formState.errors.attributes?.template_name
                      }
                      placeholder="Enter template name"
                      fullWidth
                      {...form.register("attributes.template_name", {
                        required: "Template name is required",
                        maxLength: {
                          value: DEFAULT_STRING_LENGTH,
                          message: `Template name must be less than ${DEFAULT_STRING_LENGTH} characters`,
                        },
                      })}
                    />
                    {form.formState.errors.attributes?.template_name
                      ?.message && (
                      <ErrorHint>
                        {form.formState.errors.attributes.template_name.message}
                      </ErrorHint>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="w-full">
              <Label>Description</Label>
              {isLoadingTemplate ? (
                <SkeletonPlaceholder
                  display="inline-block"
                  height="h-20"
                  width="w-full"
                />
              ) : (
                <>
                  <Textarea
                    errored={!!form.formState.errors.attributes?.description}
                    placeholder="Enter template description"
                    resizeable={false}
                    fullWidth
                    {...form.register("attributes.description", {
                      maxLength: {
                        value: DEFAULT_STRING_LENGTH,
                        message: `Description must be less than ${DEFAULT_STRING_LENGTH} characters`,
                      },
                    })}
                  />
                  {form.formState.errors.attributes?.description?.message && (
                    <ErrorHint>
                      {form.formState.errors.attributes.description.message}
                    </ErrorHint>
                  )}
                </>
              )}
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              aria-label="Save case template"
              dataLoc="modal-primary-button"
              disabled={isUpdating || !template || !form.formState.isValid}
              htmlType="submit"
              loading={isUpdating}
              role="button"
              variant="primary"
            >
              Save
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
